import { base64ToUtf8, utf8ToBase64 } from '@hub/lib/base64';
import { isRelativePath, RETURN_TO_PARAM } from './relativePath';

/**
 * `?context=` carries a base64-encoded JSON envelope minted by us, handed to an upstream (the
 * registration confirm mail), and pasted back into a link as a query parameter. The key is fixed by
 * the BFF contract — `registrationRegister`'s `context` body field.
 */
const SEARCH_CONTEXT_PARAM = 'context';

/**
 * Keys any route may promote out of an envelope. Anything else is per-route (`searchContextKeys`).
 * The matching validation lives in `baseSearchSchema`, which every declared schema is extended with.
 */
const GLOBAL_KEYS: readonly string[] = [RETURN_TO_PARAM];

/** What we put into an envelope. Primitives only — an envelope is a flat bag of search params. */
export type SearchContextPayload = Record<string, string | number | boolean | undefined>;

/**
 * Encodes an envelope for an upstream that echoes it back on a link, or `undefined` when there is
 * nothing worth carrying — callers omit the field instead of shipping an encoded `{}`, which would
 * cost a redirect on arrival and promote nothing.
 */
export function encodeSearchContext(payload: SearchContextPayload): string | undefined {
  const entries = Object.entries(payload).filter(([, value]) => value !== undefined && value !== '');
  return entries.length === 0 ? undefined : utf8ToBase64(JSON.stringify(Object.fromEntries(entries)));
}

/**
 * Unpacks `?context=` into plain search params: decode, promote the allowed keys, drop the envelope.
 * Returns the rewritten query string, or `null` when there is nothing to do — no envelope, or one
 * that doesn't decode, which is left in place rather than costing a redirect that fixes nothing.
 *
 * A one-shot expansion, by design: the envelope never reaches route search state, so nothing
 * downstream has to know it existed and nothing has to re-encode it on the way out. Dropping the
 * envelope is also what makes the redirect it triggers idempotent.
 *
 * Raw string in, raw string out. The parsed search TanStack hands to a route coerces values (a
 * numeric voucher arrives as a number), the same reason `captureBookingEntry` reads `searchStr`.
 */
export function expandSearchContext(searchStr: string, extraKeys: readonly string[] = []): string | null {
  if (!searchStr.includes(`${SEARCH_CONTEXT_PARAM}=`)) {
    return null;
  }
  const params = new URLSearchParams(searchStr);
  const encoded = params.get(SEARCH_CONTEXT_PARAM);
  if (encoded === null) {
    return null;
  }
  const payload = decodePayload(encoded);
  if (payload === null) {
    return null;
  }

  params.delete(SEARCH_CONTEXT_PARAM);
  const allowed = new Set([...GLOBAL_KEYS, ...extraKeys]);
  for (const [key, value] of Object.entries(payload)) {
    // @TODO: revisit — a param already on the URL wins, so an envelope can only fill gaps.
    if (!allowed.has(key) || params.has(key)) {
      continue;
    }
    const promoted = promotableValue(key, value);
    if (promoted !== null) {
      params.set(key, promoted);
    }
  }
  return params.toString();
}

function decodePayload(encoded: string): Record<string, unknown> | null {
  try {
    const parsed: unknown = JSON.parse(base64ToUtf8(encoded));
    return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/**
 * The envelope is authored outside the app — it rides in on a URL — so promotion is deliberately
 * narrow: an allowlisted key holding a primitive, and a `to` that resolves same-origin. Nested
 * values are dropped rather than flattened; nothing needs them, and a route's own `validateSearch`
 * is the only thing that should be deciding shapes.
 */
function promotableValue(key: string, value: unknown): string | null {
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (typeof value !== 'string' || value === '') {
    return null;
  }
  if (key === RETURN_TO_PARAM && !isRelativePath(value)) {
    return null;
  }
  return value;
}
